import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import {
    getParticipatingChallenges,
    getManagedChallenges,
    searchChallenges,
    getChallengeDetails,
    createChallenge,
    updateChallenge,
    joinChallenge,
    getChallengeInvites,
    respondChallengeInvite,
    getInvitableFriends,
    inviteToChallenge,
    getTopPublicChallenges,
} from '../api/challenges';
import {
    CHALLENGE_TYPE_DESC,
    VISIBILITY_DESC,
    STATUS_LABELS,
    VISIBILITY_LABELS,
    challengeStatItems,
} from '../constants/challenges';
import ChallengeStatGrid, { ChallengeStat } from '../components/ChallengeStatGrid';
import useToast from '../hooks/useToast';
import '../styles/ChallengesPage.css';

const TYPE_OPTIONS = [
    { value: 'coop', label: 'Co-Op' },
    { value: 'ranked', label: 'Ranked' },
];

const VISIBILITY_OPTIONS = [
    { value: 'public', label: 'Public' },
    { value: 'friends_only', label: 'Friends Only' },
    { value: 'invite_only', label: 'Invite Only' },
];

const RECURRENCE_OPTIONS = [
    { value: 0, label: 'None' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
];

const localDateTimeValue = (date) => {
    const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return shifted.toISOString().slice(0, 16);
};

const emptyForm = () => ({
    name: '',
    max_participants: '',
    visibility: '',
    challenge_type: '',
    sp_goal: '',
    start_time_utc: localDateTimeValue(new Date()),
    max_recurrences: 0,
    icon: '',
});

const formIsComplete = (form) => {
    const recurrences = Number(form.max_recurrences);
    const goal = Number(form.sp_goal);
    return Boolean(
        form.name.trim()
        && form.challenge_type
        && form.visibility
        && form.sp_goal !== ''
        && Number.isFinite(goal)
        && goal > 0
        && form.start_time_utc
        && Number.isInteger(recurrences)
        && recurrences >= 0
        && recurrences <= 5
    );
};

const recurrenceHint = (value) => {
    if (value === null || value === '') {
        return 'None runs a single week. 1–5 restarts the challenge that many extra times.';
    }
    const count = Number(value);
    if (count === 0) return 'Runs for one 7-day week.';
    return `Restarts ${count} time${count === 1 ? '' : 's'} · ${count + 1} weeks total.`;
};

const ChoiceGroup = ({ label, options, value, onChange, hint, layout = 'wrap' }) => (
    <div className="ch-field" role="radiogroup" aria-label={label}>
        <span className="ch-label">{label}</span>
        <div className={layout === 'scale' ? 'ch-scale' : 'ch-choice-group'}>
            {options.map((option) => {
                const active = value === option.value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`${layout === 'scale' ? 'ch-scale-btn' : 'ch-choice-btn'}${active ? ' is-active' : ''}`}
                        onClick={() => onChange(option.value)}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
        {hint ? <p className="ch-help">{hint}</p> : null}
    </div>
);

const StatusBadge = ({ status }) => (
    <span className={`ch-badge ch-badge--${status}`}>
        {STATUS_LABELS[status] || status}
    </span>
);

const VisibilityBadge = ({ visibility }) => (
    <span className={`ch-badge ch-badge--${visibility}`}>
        {VISIBILITY_LABELS[visibility] || visibility}
    </span>
);

const ChallengesPage = () => {
    const history = useHistory();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('participating');
    const [challenges, setChallenges] = useState([]);
    const [managedChallenges, setManagedChallenges] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchNote, setSearchNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const { error, successMessage, showSuccess, showError, clearMessages } = useToast();
    const [challengeInvites, setChallengeInvites] = useState([]);
    const [topPublicChallenges, setTopPublicChallenges] = useState([]);
    const [topPublicOffset, setTopPublicOffset] = useState(0);
    const [topPublicHasMore, setTopPublicHasMore] = useState(false);
    const [topPublicLoading, setTopPublicLoading] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [editingChallenge, setEditingChallenge] = useState(null);
    const [inviteStep, setInviteStep] = useState(false);
    const [createdChallengeId, setCreatedChallengeId] = useState(null);
    const [invitableFriends, setInvitableFriends] = useState([]);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef(null);
    const [formData, setFormData] = useState(emptyForm);

    const fetchData = useCallback(async () => {
        try {
            const [participatingData, managedData, invitesData] = await Promise.all([
                getParticipatingChallenges(),
                getManagedChallenges(),
                getChallengeInvites(),
            ]);
            setChallenges(Array.isArray(participatingData) ? participatingData : []);
            setManagedChallenges(Array.isArray(managedData) ? managedData : []);
            setChallengeInvites(Array.isArray(invitesData) ? invitesData : []);
        } catch (err) {
            showError('Failed to load challenges.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (location.state?.openChallengeId) {
            history.push(`/challenges/${location.state.openChallengeId}`);
        } else if (location.state?.editChallengeId) {
            (async () => {
                try {
                    const data = await getChallengeDetails(location.state.editChallengeId);
                    openEditForm(data);
                } catch {
                    // ignore
                }
            })();
            history.replace('/challenges');
        }
    }, [location.state]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = async () => {
        try {
            setSearchLoading(true);
            clearMessages();
            setSearchNote('');
            const data = await searchChallenges(searchQuery.trim());
            const results = Array.isArray(data) ? data : [];
            setSearchResults(results);
            setSearchNote(results.length === 0 ? 'No challenges found.' : '');
        } catch (err) {
            showError(err.message || 'Search failed.');
        } finally {
            setSearchLoading(false);
        }
    };

    const loadTopPublicChallenges = async (offset = 0) => {
        try {
            setTopPublicLoading(true);
            const data = await getTopPublicChallenges(offset);
            const next = data.challenges || [];
            if (offset === 0) {
                setTopPublicChallenges(next);
            } else {
                setTopPublicChallenges((prev) => [...prev, ...next]);
            }
            setTopPublicOffset(offset + next.length);
            setTopPublicHasMore(data.has_more || false);
        } catch (err) {
            // Discovery list is optional; the rest of the page still works.
        } finally {
            setTopPublicLoading(false);
        }
    };

    const selectTab = (tab) => {
        setActiveTab(tab);
        clearMessages();
        if (tab === 'find' && topPublicChallenges.length === 0) {
            loadTopPublicChallenges(0);
        }
    };

    const handleViewDetails = (challengeId) => {
        history.push(`/challenges/${challengeId}`);
    };

    const handleJoin = async (challengeId) => {
        try {
            clearMessages();
            await joinChallenge(challengeId);
            showSuccess('Successfully joined the challenge!');
            await fetchData();
            setSearchResults((prev) => prev.filter((c) => c.challenge_id !== challengeId));
            setTopPublicChallenges((prev) => prev.filter((c) => c.challenge_id !== challengeId));
        } catch (err) {
            showError(err.message || 'Failed to join challenge.');
        }
    };

    const handleInviteRespond = async (challengeId, status) => {
        try {
            clearMessages();
            await respondChallengeInvite(challengeId, status);
            setChallengeInvites((prev) => prev.filter((inv) => inv.challenge.challenge_id !== challengeId));
            showSuccess(status === 'accepted' ? 'Successfully joined the challenge!' : 'Invite declined.');
            window.dispatchEvent(new CustomEvent('challengeInvitesUpdated'));
            await fetchData();
        } catch (err) {
            showError(err.message || 'Failed to respond to invite.');
        }
    };

    const closeForm = () => {
        setInviteStep(false);
        setShowForm(false);
        setEditingChallenge(null);
        setCreatedChallengeId(null);
        setInvitableFriends([]);
        setShowEmojiPicker(false);
        clearMessages();
    };

    const openCreateForm = () => {
        setEditingChallenge(null);
        setInviteStep(false);
        setFormData(emptyForm());
        setShowForm(true);
        clearMessages();
    };

    const openEditForm = (challenge) => {
        setEditingChallenge(challenge);
        setInviteStep(false);
        const startDate = challenge.start_time_utc
            ? localDateTimeValue(new Date(challenge.start_time_utc))
            : '';
        setFormData({
            name: challenge.name || '',
            max_participants: challenge.max_participants || '',
            visibility: challenge.visibility || 'public',
            challenge_type: challenge.challenge_type === 'threshold'
                ? 'ranked'
                : (challenge.challenge_type || 'coop'),
            sp_goal: challenge.sp_goal || '',
            start_time_utc: startDate,
            max_recurrences: Number(challenge.max_recurrences ?? 0),
            icon: challenge.icon || '',
        });
        setShowForm(true);
        clearMessages();
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!formIsComplete(formData) || saving) return;
        clearMessages();

        const payload = {
            name: formData.name.trim(),
            duration: 7,
            max_participants: formData.max_participants ? parseInt(formData.max_participants, 10) : null,
            visibility: formData.visibility,
            challenge_type: formData.challenge_type,
            sp_goal: parseFloat(formData.sp_goal),
            start_time_utc: new Date(formData.start_time_utc).toISOString(),
            max_recurrences: Number(formData.max_recurrences),
            icon: formData.icon.trim() || null,
        };

        try {
            setSaving(true);
            if (editingChallenge) {
                await updateChallenge(editingChallenge.challenge_id, payload);
                showSuccess('Challenge updated!');
                setShowForm(false);
                setEditingChallenge(null);
            } else {
                const created = await createChallenge(payload);
                const newId = created.challenge_id;
                setCreatedChallengeId(newId);
                setInviteLoading(true);
                try {
                    const friends = await getInvitableFriends(newId);
                    setInvitableFriends(Array.isArray(friends) ? friends : []);
                } catch {
                    setInvitableFriends([]);
                } finally {
                    setInviteLoading(false);
                }
                setInviteStep(true);
            }
            await fetchData();
        } catch (err) {
            showError(err.message || 'Failed to save challenge.');
        } finally {
            setSaving(false);
        }
    };

    const handleInviteFromForm = async (friendId, username) => {
        try {
            await inviteToChallenge(createdChallengeId, friendId);
            showSuccess(`Invite sent to ${username || 'user'}!`);
            setInvitableFriends((prev) => prev.map((friend) => (
                friend.auth_id === friendId ? { ...friend, invite_status: 'pending' } : friend
            )));
        } catch (err) {
            showError(err.message || 'Failed to send invite.');
        }
    };

    const renderChallengeCard = (challenge, { note, actions } = {}) => (
        <article key={challenge.challenge_id} className="ch-card">
            <div className="ch-card-main">
                <div
                    className="ch-card-open"
                    role="link"
                    tabIndex={0}
                    onClick={() => handleViewDetails(challenge.challenge_id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewDetails(challenge.challenge_id);
                        }
                    }}
                >
                    <div className="ch-card-icon" aria-hidden="true">{challenge.icon || '🏆'}</div>
                    <div className="ch-card-copy">
                        <div className="ch-card-title-row">
                            <h3 className="ch-card-title">{challenge.name}</h3>
                            {challenge.user_role && (
                                <span className={`ch-role ch-role--${challenge.user_role}`}>
                                    {challenge.user_role === 'admin' ? 'Admin' : 'Member'}
                                </span>
                            )}
                        </div>
                        <div className="ch-badges">
                            <StatusBadge status={challenge.computed_status} />
                            <VisibilityBadge visibility={challenge.visibility} />
                        </div>
                        {note}
                    </div>
                </div>
                {actions ? <div className="ch-card-actions">{actions}</div> : null}
            </div>
            <div className="ch-card-stats">
                <ChallengeStatGrid stats={challengeStatItems(challenge)} compact label={`${challenge.name} summary`} />
            </div>
        </article>
    );

    const renderForm = () => {
        const nameDone = formData.name.trim().length > 0;
        const typeDone = Boolean(formData.challenge_type);
        const visibilityDone = Boolean(formData.visibility);
        const goal = Number(formData.sp_goal);
        const goalDone = formData.sp_goal !== '' && Number.isFinite(goal) && goal > 0;
        const startDone = Boolean(formData.start_time_utc);
        const canSubmit = formIsComplete(formData) && !saving;
        const optionalOpen = Number(formData.max_recurrences) > 0 || Boolean(formData.max_participants);

        return (
            <form onSubmit={handleFormSubmit} className="ch-form">
                <div className="ch-name-row">
                    <div className="ch-field ch-field--icon">
                        <span className="ch-label">Icon</span>
                        <div className="ch-icon-wrap" ref={emojiPickerRef}>
                            <button
                                type="button"
                                className="ch-icon-btn"
                                onClick={() => setShowEmojiPicker((prev) => !prev)}
                                aria-label="Choose an icon"
                            >
                                {formData.icon || '😀'}
                            </button>
                            {formData.icon && (
                                <button
                                    type="button"
                                    className="ch-icon-clear"
                                    onClick={() => setFormData((prev) => ({ ...prev, icon: '' }))}
                                    aria-label="Remove icon"
                                >
                                    &times;
                                </button>
                            )}
                            {showEmojiPicker && (
                                <div className="ch-emoji-popover">
                                    <EmojiPicker
                                        onEmojiClick={(emojiData) => {
                                            setFormData((prev) => ({ ...prev, icon: emojiData.emoji }));
                                            setShowEmojiPicker(false);
                                        }}
                                        width="100%"
                                        height={360}
                                        searchPlaceholder="Search emoji..."
                                        previewConfig={{ showPreview: false }}
                                        skinTonesDisabled
                                        autoFocusSearch={false}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="ch-field">
                        <label className="ch-label" htmlFor="challenge-name">Name</label>
                        <input
                            id="challenge-name"
                            className="ch-input"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                            required
                            placeholder="Challenge name"
                            autoComplete="off"
                        />
                    </div>
                </div>

                {nameDone && (
                    <ChoiceGroup
                        label="Challenge type"
                        options={TYPE_OPTIONS}
                        value={formData.challenge_type}
                        onChange={(value) => setFormData((prev) => ({ ...prev, challenge_type: value }))}
                        hint={CHALLENGE_TYPE_DESC[formData.challenge_type]}
                    />
                )}

                {nameDone && typeDone && (
                    <ChoiceGroup
                        label="Visibility"
                        options={VISIBILITY_OPTIONS}
                        value={formData.visibility}
                        onChange={(value) => setFormData((prev) => ({ ...prev, visibility: value }))}
                        hint={VISIBILITY_DESC[formData.visibility]}
                    />
                )}

                {nameDone && typeDone && visibilityDone && (
                    <div className="ch-field">
                        <label className="ch-label" htmlFor="challenge-goal">SP goal</label>
                        <input
                            id="challenge-goal"
                            className="ch-input"
                            type="number"
                            name="sp_goal"
                            value={formData.sp_goal}
                            onChange={(e) => setFormData((prev) => ({ ...prev, sp_goal: e.target.value }))}
                            required
                            min="1"
                            step="any"
                            inputMode="decimal"
                            placeholder="e.g. 1000"
                        />
                        <p className="ch-help">SpudPoints the challenge is aiming for.</p>
                    </div>
                )}

                {goalDone && (
                    <div className="ch-field">
                        <label className="ch-label" htmlFor="challenge-start">Start</label>
                        <input
                            id="challenge-start"
                            className="ch-input"
                            type="datetime-local"
                            name="start_time_utc"
                            value={formData.start_time_utc}
                            onChange={(e) => setFormData((prev) => ({ ...prev, start_time_utc: e.target.value }))}
                            required
                        />
                        <p className="ch-help">The challenge runs for 7 days from this time.</p>
                    </div>
                )}

                {goalDone && startDone && (
                    <details className="ch-optional" defaultOpen={optionalOpen}>
                        <summary className="ch-optional-toggle">
                            <span>Additional details</span>
                            <span className="ch-optional-hint">Optional</span>
                        </summary>
                        <div className="ch-optional-body">
                            <ChoiceGroup
                                label="Recurrences"
                                layout="scale"
                                options={RECURRENCE_OPTIONS}
                                value={formData.max_recurrences}
                                onChange={(value) => setFormData((prev) => ({ ...prev, max_recurrences: value }))}
                                hint={recurrenceHint(formData.max_recurrences)}
                            />
                            <div className="ch-field">
                                <label className="ch-label" htmlFor="challenge-cap">Max participants</label>
                                <input
                                    id="challenge-cap"
                                    className="ch-input"
                                    type="number"
                                    name="max_participants"
                                    value={formData.max_participants}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, max_participants: e.target.value }))}
                                    min="2"
                                    inputMode="numeric"
                                    placeholder="Unlimited"
                                />
                            </div>
                        </div>
                    </details>
                )}

                <button type="submit" className="ch-submit" disabled={!canSubmit}>
                    {saving ? 'Saving...' : editingChallenge ? 'Save Changes' : 'Create Challenge'}
                </button>
            </form>
        );
    };

    const renderInviteStep = () => (
        <div className="ch-form">
            <div className="ch-invite-hero">
                <div className="ch-invite-check" aria-hidden="true">✓</div>
                <p className="ch-note">Invite friends now, or come back to it later.</p>
            </div>
            {inviteLoading ? (
                <div className="sc-loading">
                    <div className="sc-spinner"></div>
                    <p>Loading friends...</p>
                </div>
            ) : invitableFriends.length === 0 ? (
                <p className="ch-empty">No friends available to invite right now.</p>
            ) : (
                <div className="ch-person-list">
                    {invitableFriends.map((friend) => (
                        <div key={friend.auth_id} className="ch-person">
                            <span className="ch-person-name">{friend.username || friend.auth_id}</span>
                            {friend.invite_status === 'pending' ? (
                                <span className="ch-person-status">Pending</span>
                            ) : (
                                <button
                                    type="button"
                                    className="sc-btn sc-btn--primary sc-btn--sm"
                                    onClick={() => handleInviteFromForm(friend.auth_id, friend.username)}
                                >
                                    Invite
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className="ch-inline-actions">
                <button
                    type="button"
                    className="sc-btn sc-btn--primary"
                    onClick={() => history.push(`/challenges/${createdChallengeId}`)}
                >
                    View Challenge
                </button>
                <button type="button" className="sc-btn sc-btn--secondary" onClick={closeForm}>
                    Done
                </button>
            </div>
        </div>
    );

    if (loading && !showForm) {
        return (
            <div className="sc-page">
                <div className="sc-container">
                    <div className="sc-loading">
                        <div className="sc-spinner"></div>
                        <p>Loading challenges...</p>
                    </div>
                </div>
            </div>
        );
    }

    const activeChallenges = challenges.filter((c) => c.computed_status !== 'completed');
    const activeManaged = managedChallenges.filter((c) => c.computed_status !== 'completed');
    const doneChallenges = challenges.filter((c) => c.computed_status === 'completed');

    const tabs = [
        { id: 'participating', label: 'Mine', value: activeChallenges.length, unit: 'active' },
        { id: 'managed', label: 'Managing', value: activeManaged.length, unit: 'active' },
        { id: 'completed', label: 'Done', value: doneChallenges.length, unit: 'finished' },
        { id: 'find', label: 'Find', value: challengeInvites.length, unit: 'invites' },
    ];

    const pageTitle = inviteStep
        ? 'Invite Friends'
        : editingChallenge
            ? 'Edit Challenge'
            : 'Create Challenge';

    return (
        <div className="sc-page">
            <div className="sc-container ch-shell">
                {showForm && (
                    <div className="ch-back-bar">
                        <button type="button" className="sc-back-link" onClick={closeForm}>
                            &larr; Challenges
                        </button>
                    </div>
                )}

                <header className="sc-header ch-header">
                    <div className="ch-header-text">
                        <h2>{showForm ? pageTitle : 'Challenges'}</h2>
                        <p className="ch-header-sub">
                            {inviteStep
                                ? 'Your challenge is ready'
                                : showForm
                                    ? 'Each challenge runs for 7 days'
                                    : 'Week-long goals with friends'}
                        </p>
                    </div>
                    {!showForm && (
                        <button type="button" className="sc-btn sc-btn--primary sc-btn--sm" onClick={openCreateForm}>
                            New Challenge
                        </button>
                    )}
                </header>

                {(error || successMessage) && (
                    <div className="ch-alerts">
                        {error && <div className="sc-alert sc-alert--error">{error}</div>}
                        {successMessage && <div className="sc-alert sc-alert--success">{successMessage}</div>}
                    </div>
                )}

                {showForm ? (
                    <div className="ch-content">
                        {inviteStep ? renderInviteStep() : renderForm()}
                    </div>
                ) : (
                    <>
                        <section className="ch-stats ch-stats--tabs" role="tablist" aria-label="Challenge lists">
                            {tabs.map((tab) => (
                                <ChallengeStat
                                    key={tab.id}
                                    label={tab.label}
                                    value={tab.value}
                                    unit={tab.unit}
                                    active={activeTab === tab.id}
                                    role="tab"
                                    aria-selected={activeTab === tab.id}
                                    onClick={() => selectTab(tab.id)}
                                />
                            ))}
                        </section>

                        <div className="ch-content">
                            {activeTab === 'participating' && (
                                activeChallenges.length === 0 ? (
                                    <p className="ch-empty">
                                        You&apos;re not in any active challenges. Use Find to discover one, or create your own.
                                    </p>
                                ) : (
                                    <div className="ch-list">
                                        {activeChallenges.map((challenge) => renderChallengeCard(challenge))}
                                    </div>
                                )
                            )}

                            {activeTab === 'managed' && (
                                activeManaged.length === 0 ? (
                                    <p className="ch-empty">
                                        You&apos;re not managing any active challenges. Create one to get started.
                                    </p>
                                ) : (
                                    <div className="ch-list">
                                        {activeManaged.map((challenge) => renderChallengeCard(challenge))}
                                    </div>
                                )
                            )}

                            {activeTab === 'completed' && (
                                doneChallenges.length === 0 ? (
                                    <p className="ch-empty">No completed challenges yet.</p>
                                ) : (
                                    <div className="ch-list">
                                        {doneChallenges.map((challenge) => renderChallengeCard(challenge))}
                                    </div>
                                )
                            )}

                            {activeTab === 'find' && (
                                <>
                                    {challengeInvites.length > 0 && (
                                        <section className="ch-section">
                                            <h3 className="ch-section-title">Pending invites</h3>
                                            <div className="ch-list">
                                                {challengeInvites.map((invite) => renderChallengeCard(invite.challenge, {
                                                    note: (
                                                        <p className="ch-card-note">
                                                            Invited by <strong>{invite.inviter_username || 'a friend'}</strong>
                                                        </p>
                                                    ),
                                                    actions: (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="sc-btn sc-btn--primary sc-btn--sm"
                                                                onClick={() => handleInviteRespond(invite.challenge.challenge_id, 'accepted')}
                                                            >
                                                                Join
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="sc-btn sc-btn--danger sc-btn--sm"
                                                                onClick={() => handleInviteRespond(invite.challenge.challenge_id, 'declined')}
                                                            >
                                                                Decline
                                                            </button>
                                                        </>
                                                    ),
                                                }))}
                                            </div>
                                        </section>
                                    )}

                                    <section className="ch-section">
                                        <h3 className="ch-section-title">Search</h3>
                                        <div className="ch-search">
                                            <input
                                                className="ch-input"
                                                type="text"
                                                placeholder="Search challenges by name..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSearch();
                                                }}
                                            />
                                            <button
                                                type="button"
                                                className="sc-btn sc-btn--primary"
                                                onClick={handleSearch}
                                                disabled={searchLoading}
                                            >
                                                {searchLoading ? 'Searching...' : 'Search'}
                                            </button>
                                        </div>
                                        {searchNote && <p className="ch-empty">{searchNote}</p>}
                                        {searchResults.length > 0 && (
                                            <div className="ch-list">
                                                {searchResults.map((challenge) => renderChallengeCard(challenge, {
                                                    actions: (
                                                        <button
                                                            type="button"
                                                            className="sc-btn sc-btn--primary sc-btn--sm"
                                                            onClick={() => handleJoin(challenge.challenge_id)}
                                                        >
                                                            Join
                                                        </button>
                                                    ),
                                                }))}
                                            </div>
                                        )}
                                    </section>

                                    <section className="ch-section">
                                        <h3 className="ch-section-title">Top public challenges</h3>
                                        {topPublicLoading && topPublicChallenges.length === 0 ? (
                                            <div className="sc-loading">
                                                <div className="sc-spinner"></div>
                                                <p>Loading top challenges...</p>
                                            </div>
                                        ) : topPublicChallenges.length === 0 ? (
                                            <p className="ch-empty">No public challenges available right now.</p>
                                        ) : (
                                            <>
                                                <div className="ch-list">
                                                    {topPublicChallenges.map((challenge) => renderChallengeCard(challenge, {
                                                        actions: (
                                                            <button
                                                                type="button"
                                                                className="sc-btn sc-btn--primary sc-btn--sm"
                                                                onClick={() => handleJoin(challenge.challenge_id)}
                                                            >
                                                                Join
                                                            </button>
                                                        ),
                                                    }))}
                                                </div>
                                                {topPublicHasMore && (
                                                    <button
                                                        type="button"
                                                        className="ch-more"
                                                        onClick={() => loadTopPublicChallenges(topPublicOffset)}
                                                        disabled={topPublicLoading}
                                                    >
                                                        {topPublicLoading ? 'Loading...' : 'Load more'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </section>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChallengesPage;
